"use strict";
/**
 * sl-pbux.8.6 / sl-sbux.8.2: Performance-budget constants shared by builder
 * runtime + CI.
 *
 * Source of truth lives in `packages/frontend/apps/custom-app-viewer/budgets.json`.
 * That file is consumed by `scripts/check-budgets.sh` for the post-build
 * bundle-size gate; this module re-exports the numbers as typed constants
 * so the renderer + builder can enforce them at write time without parsing
 * JSON at runtime.
 *
 * Enforcement map:
 *   - customAppViewerBundleGzipBytes  -> CI (`scripts/check-budgets.sh`).
 *   - customAppCssOutputSoftWarnBytes      -> validation panel (`get-custom-app-validation-issues.ts`)
 *                                        + vitest regression (`style-css-budget.spec.ts`).
 *   - customAppCssOutputHardBlockBytes     -> MaterializationService publish path
 *                                        (`BuilderBudgetExceededError` throw site).
 *   - criticalCssInlineBudget         -> Beasties extraction target — caps
 *                                        the above-fold subset that ships
 *                                        inline in `<style>` ahead of the
 *                                        async-preloaded full stylesheet.
 *   - treeNodeCountSoftWarn           -> validation panel (`get-custom-app-validation-issues.ts`).
 *   - treeNodeCountHardBlock          -> ViewConfigStore.{addComponent,addComponentAt,serializeSnapshot}.
 *   - styleVariantsPerCustomAppSoftWarn    -> validation panel.
 *   - styleVariantsPerCustomAppHardBlock   -> StyleVariant editor save path (variant-editor-modal).
 *
 * Both soft- and hard-thresholds for the tree are expressed in
 * `components.length` (ViewComponentInstance count), not full snapshot
 * `nodes.length` — the snapshot adds a constant +2..+5 infra-nodes (page-
 * shape root + slot nodes) that the author cannot influence.
 *
 * The CSS budget is two-tiered: `customAppCssOutputSoftWarnBytes` surfaces in the
 * editor as a warning (authors stay productive while approaching the inline
 * sweet-spot), while `customAppCssOutputHardBlockBytes` is a sanity ceiling that
 * fails the publish path to prevent abusive multi-MB blobs from reaching
 * Bunny-CDN. Between the two thresholds Beasties extracts the
 * `criticalCssInlineBudget`-sized above-fold subset so LCP stays optimal
 * regardless of total stylesheet size.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BuilderBudgetExceededError = exports.BUILDER_BUDGETS = void 0;
exports.BUILDER_BUDGETS = {
    /** Gzipped JS bundle size for /p/ pages — CI gate only. */
    customAppViewerBundleGzipBytes: 300_000,
    /**
     * Soft-warn threshold for the merged minified CSS output of a published
     * site. Authors keep publishing above this number; the validation panel
     * surfaces a warning so they can trim unused sheets.
     */
    customAppCssOutputSoftWarnBytes: 50_000,
    /**
     * Hard-block for the merged minified CSS output. The
     * MaterializationService throws `BuilderBudgetExceededError` when the
     * concatenated theme + motion + style + custom-sheet CSS exceeds this
     * size — keeps a runaway author or a misbehaving Copilot from shipping
     * megabytes of stylesheet through the CDN pipeline.
     */
    customAppCssOutputHardBlockBytes: 300_000,
    /**
     * Above-fold CSS budget Beasties targets when extracting the inline
     * `criticalCss` snapshot field. Matches Beasties' library default and
     * the empirical 14 KB sweet spot that maximises LCP without sending
     * render-blocking bytes the visitor doesn't immediately see.
     */
    criticalCssInlineBudget: 14_000,
    /** Soft-warn threshold for ViewComponentInstance count. */
    treeNodeCountSoftWarn: 100,
    /**
     * Hard-block — Builder rejects component additions and snapshot writes
     * once the ViewComponentInstance count would exceed this value.
     */
    treeNodeCountHardBlock: 500,
    /** Soft-warn threshold for total StyleVariant count on the CustomAppConfig. */
    styleVariantsPerCustomAppSoftWarn: 50,
    /** Hard-block — StyleVariant editor refuses to persist above this count. */
    styleVariantsPerCustomAppHardBlock: 200,
};
/**
 * Builder-scoped budget violation. Named distinctly so it never collides
 * with the unrelated AI-provider `BudgetExceededError` in
 * `core/blocksuite/ai/provider/error.ts`.
 */
class BuilderBudgetExceededError extends Error {
    constructor(args) {
        const message = `sl-pbux budget exceeded — ${args.budget}: actual=${args.actual} ` +
            `limit=${args.limit}${args.detail ? ` (${args.detail})` : ''}`;
        super(message);
        // Defense-in-depth: the workspace targets ES2024 where `class extends
        // Error` natively preserves the prototype chain, so this call is a
        // no-op today. Kept as a one-line guarantee that `instanceof
        // BuilderBudgetExceededError` survives any future bundler/transpiler
        // that downlevel-emits this package.
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = 'BuilderBudgetExceededError';
        this.budget = args.budget;
        this.limit = args.limit;
        this.actual = args.actual;
    }
}
exports.BuilderBudgetExceededError = BuilderBudgetExceededError;
